import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios8200 from "../services/axios8200";
import axios8001 from "../services/axios8001";
import axios8000 from "../services/8000AxiosInstance";
import "./Opportunity.css";
import OpportunityNavbar from "./OpportunityNavbar";
import askCoachIcon from "../assets/icons/ask-coach-icon.png";
import SidebarButton from "./SidebarButton"; // Adjust path if needed
import { PieChart as RechartsPieChart, Pie, Cell, Tooltip, BarChart as RechartsBarChart, Bar, XAxis, YAxis, Legend, ResponsiveContainer } from "recharts";
import OpportunityDetailsForm from "./OpportunityDetailsForm";
import ClosingProbabilityTile from "./ClosingProbabilityTile";
import UpdateMeddiccModal from "./UpdateMeddiccModal";


// Constants
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#FF4081", "#9C27B0", '#00C49F', '#D3D3D3'];

// Custom Pie Chart Component
const CustomPieChart = ({ data }) => (
  <RechartsPieChart width={120} height={120}>
    <Pie
      data={data}
      dataKey="value"
      nameKey="stage"
      cx="50%"
      cy="50%"
      outerRadius={50}
      innerRadius={30}
      fill="#8884d8"
      label
    >
      {data.map((entry, index) => (
        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
      ))}
    </Pie>
    <Tooltip />
  </RechartsPieChart>
);

// Custom Bar Chart Component
const CustomBarChart = ({ data }) => (
  <RechartsBarChart width={200} height={150} data={data} layout="vertical">
    <XAxis type="number" />
    <YAxis dataKey="status" type="category" />
    <Tooltip />
    <Legend />
    <Bar dataKey="value" fill="#82ca9d" />
  </RechartsBarChart>
);

const OpportunityDetailsView = ({
  details,
  customerId,
  opportunityId,
  stage,
  solutionType,
  onEditClick,
}) => (
  <>
    <p>Customer ID: {customerId}</p>
    <p>Opportunity ID: {opportunityId}</p>
    <p>Customer Name: {details?.customer_name || "Loading..."}</p>
    <p>Opportunity Name: {details?.opportunity_name || "N/A"}</p>
    <p>Contact: {details?.contact || "N/A"}</p>
    <p>Champions: {details?.champions || "N/A"}</p>
    <p>Sales Stage: {stage || "N/A"}</p>
    <p>Solution Type: {solutionType || "N/A"}</p>
    <p>Vendor: {details?.vendor || "N/A"}</p>
    <p>
      Commit Date:{" "}
      {details?.commit_date
        ? new Date(details.commit_date).toLocaleDateString()
        : "N/A"}
    </p>
    <p>Value: {details?.value ? `$${details.value.toLocaleString()}` : "N/A"}</p>
    <button
      className="edit-button"
      onClick={onEditClick}
      aria-label="Edit Opportunity Details"
    >
      Edit
    </button>
  </>
);



const Opportunity = () => {
  const { opportunity_id: urlOpportunityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const customerIdFromUrl = searchParams.get("customer_id");
  const urlCustomerName = searchParams.get("customer_name");
  const urlOpportunityName = searchParams.get("opportunity_name");
  

  const [opportunityId, setOpportunityId] = useState(urlOpportunityId || "");
  const [opportunityDetails, setOpportunityDetails] = useState(null);
  const [stage, setStage] = useState("");
  const [solutionType, setSolutionType] = useState("");
  const [metrics, setMetrics] = useState({});
  const [tasks, setTasks] = useState([]);
  const [learnings, setLearnings] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showAskCoachTile, setShowAskCoachTile] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const limitedTasks = tasks.slice(0, 10);
  const [nextStepsModalVisible, setNextStepsModalVisible] = useState(false);
  const [nextSteps, setNextSteps] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [opportunityName, setOpportunityName] = useState("");
  const [contact, setContact] = useState("");
  const [champions, setChampions] = useState("");
  const [vendor, setVendor] = useState("");
  const [newUpdate, setNewUpdate] = useState('');
  const [customerId, setCustomerId] = useState("");
  

  // Missing State Variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [loadingNextSteps, setLoadingNextSteps] = useState(false);
  const [errorNextSteps, setErrorNextSteps] = useState(null);
  const [suggestedNextSteps, setSuggestedNextSteps] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [updateTask, setUpdateTask] = useState(null);
  const [suggestedTasks, setSuggestedTasks] = useState([]); // Suggested tasks state
  const [showAskCoachModal, setShowAskCoachModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [createTaskModalVisible, setCreateTaskModalVisible] = useState(false);
  const [showUpdateMeddiccModal, setShowUpdateMeddiccModal] = useState(false);
  

  

  // Retrieve user ID and API Key
  const userId = sessionStorage.getItem("user_id");
  const apiKey = sessionStorage.getItem("api_key");

  // Redirect to login if missing credentials
  useEffect(() => {
    if (!userId || !apiKey) {
      console.error("Missing user ID or API key. Redirecting to login.");
      navigate("/login");
    }
  }, [userId, apiKey, navigate]);
  
  // Define contexts for Ask Coach buttons
  const contexts = {
    "Lead Next Steps": "Provide personalized next steps for nurturing the lead and progressing the opportunity.",
    "Fetch MEDDICC": "Retrieve and validate key MEDDICC components for this opportunity.",
    "Competitor Insights": "Analyze potential competitors in this opportunity and suggest strategies.",
    "Risk Mitigation": "Identify potential risks and recommend actions to mitigate them.",
    "Analyze Tasks": "Assess pending tasks for the opportunity to ensure alignment with sales strategy.",
    "Persona Analysis": "Analyze the personas involved and suggest tailored messaging and actions.",
  };

  const handleSendMessageWithContext = async (buttonKey) => {
    if (!contexts[buttonKey]) {
      console.error(`Invalid button key: ${buttonKey}`);
      setChatMessages((prevMessages) => [
        ...prevMessages,
        { role: "error", content: "Error: Invalid request." },
      ]);
      return;
    }

    const question = contexts[buttonKey];
    const payload = {
      question,
      opportunity_id: opportunityId || undefined,
      api_key: apiKey, // Retrieve dynamically
      user_id: userId, // Retrieve dynamically
    };

    setChatMessages((prevMessages) => [
      ...prevMessages,
      { role: "assistant", content: "Processing your request..." },
    ]);
    setLoadingChat(true);

    try {
      const response = await axios8000.post("/ask", payload);
      setChatMessages((prevMessages) => [
        ...prevMessages,
        { role: "assistant", content: response.data?.data?.response || "No response received." },
      ]);
    } catch (error) {
      console.error("Error:", error);
      setChatMessages((prevMessages) => [
        ...prevMessages,
        { role: "error", content: "An error occurred. Please try again." },
      ]);
    } finally {
      setLoadingChat(false);
    }
  };

  
  // Function to handle sending payload to your chat API
  const sendToChatAPI = async (payload) => {
    try {
      const response = await axios8000.post("/ask", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("Message sent successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  };
  
  
  
  const OpportunityNavbar = ({
    setShowAskCoachModal,
    setCreateTaskModalVisible,
    setSearchModalVisible,
    setNextStepsModalVisible,
    setShowAnalyzeTaskModal,
    setShowUpdateMeddiccModal, // Added prop for Update MEDDICC modal
    generateTasks,
    fetchOpportunityNextSteps,
  }) => {
    const [showMobileMenu, setShowMobileMenu] = useState(false);
  
    // Shared button handler logic
    const handleButtonClick = async (action) => {
      switch (action) {
        case "ask-coach":
          setShowAskCoachModal(true);
          break;
        case "generate-tasks":
          await generateTasks();
          break;
        case "search-tasks":
          setSearchModalVisible(true);
          break;
        case "create-new-task":
          setCreateTaskModalVisible(true);
          break;
        case "opportunity-next-steps":
          await fetchOpportunityNextSteps();
          setNextStepsModalVisible(true);
          break;
        case "analyze-tasks":
          if (setShowAnalyzeTaskModal) {
            setShowAnalyzeTaskModal(true);
          } else {
            console.error("setShowAnalyzeTaskModal is not provided");
          }
          break;
        case "update-meddicc": // Handle Update MEDDICC button
          if (setShowUpdateMeddiccModal) {
            setShowUpdateMeddiccModal(true);
          } else {
            console.error("setShowUpdateMeddiccModal is not provided");
          }
          break;
        case "logout":
          sessionStorage.clear(); // Clear session storage
          window.location.href = "/"; // Redirect to the homepage
          break;
        default:
          console.warn(`Unhandled action: ${action}`);
      }
    };
  
    return (
      <div className="navbar-top-container">
        {/* Desktop Navbar */}
        <div className="desktop-navbar">
          <button
            className="navbar-button"
            onClick={() => handleButtonClick("ask-coach")}
          >
            <img
              src={askCoachIcon}
              alt="Ask Coach"
              className="ask-coach-icon-navbar"
            />
            Ask Coach
          </button>
          <button
            className="navbar-button"
            onClick={() => handleButtonClick("generate-tasks")}
          >
            Generate Tasks
          </button>
          <button
            className="navbar-button"
            onClick={() => handleButtonClick("search-tasks")}
          >
            Search Tasks
          </button>
          <button
            className="navbar-button"
            onClick={() => handleButtonClick("create-new-task")}
          >
            Create New Task
          </button>
          <button
            className="navbar-button"
            onClick={() => handleButtonClick("opportunity-next-steps")}
          >
            Opportunity Next Steps
          </button>
          <button
            className="navbar-button"
            onClick={() => handleButtonClick("analyze-tasks")}
          >
            Analyze Tasks
          </button>
          <button
            className="navbar-button"
            onClick={() => handleButtonClick("update-meddicc")}
          >
            Update MEDDICC
          </button>
          <button
            className="navbar-button"
            onClick={() => handleButtonClick("logout")}
          >
            Logout
          </button>
        </div>
  
        {/* Mobile Navbar */}
        <div className="mobile-navbar">
          <button
            className="hamburger-menu"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            ☰
          </button>
          {showMobileMenu && (
            <div className="mobile-menu">
              <button
                className="navbar-button"
                onClick={() => handleButtonClick("ask-coach")}
              >
                Ask Coach
              </button>
              <button
                className="navbar-button"
                onClick={() => handleButtonClick("generate-tasks")}
              >
                Generate Tasks
              </button>
              <button
                className="navbar-button"
                onClick={() => handleButtonClick("search-tasks")}
              >
                Search Tasks
              </button>
              <button
                className="navbar-button"
                onClick={() => handleButtonClick("create-new-task")}
              >
                Create New Task
              </button>
              <button
                className="navbar-button"
                onClick={() => handleButtonClick("opportunity-next-steps")}
              >
                Opportunity Next Steps
              </button>
              <button
                className="navbar-button"
                onClick={() => handleButtonClick("analyze-tasks")}
              >
                Analyze Tasks
              </button>
              <button
                className="navbar-button"
                onClick={() => handleButtonClick("update-meddicc")}
              >
                Update MEDDICC
              </button>
              <button
                className="navbar-button"
                onClick={() => handleButtonClick("logout")}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };
  

  
  const OpportunityDetailsView = ({ details, customerId, opportunityId, onEditClick }) => (
    <>
      <p>Customer ID: {customerId}</p>
      <p>Opportunity ID: {opportunityId}</p>
      <p>Customer Name: {details?.customer_name || 'Loading...'}</p>
      <p>Opportunity Name: {details?.opportunity_name || 'N/A'}</p>
      <p>Contact: {details?.contacts?.join(", ") || 'N/A'}</p> {/* Handle array */}
      <p>Champions: {details?.champions?.join(", ") || 'N/A'}</p> {/* Handle array */}
      <p>Sales Stage: {details?.stage || 'N/A'}</p>
      <p>Solution Type: {details?.solution_type || 'N/A'}</p>
      <p>Vendor: {details?.vendor || 'N/A'}</p>
      <p>Commit Date: {details?.commit_date ? new Date(details.commit_date).toLocaleDateString() : 'N/A'}</p>
      <p>Value: {details?.value ? `$${details.value.toLocaleString()}` : 'N/A'}</p>
      <button className="edit-button" onClick={onEditClick} aria-label="Edit Opportunity Details">
        Edit
      </button>
    </>
  );
  
  

  // Save updated details to backend
const saveUpdatedDetails = async (updatedDetails) => {
  try {
    const userId = sessionStorage.getItem("user_id");
    const apiKey = sessionStorage.getItem("api_key");

    if (!userId || !apiKey) {
      console.error("Missing user ID or API Key.");
      return;
    }

    const { contact, champions, stage, solutionType, vendor, commit_date, value } = updatedDetails;

    // Payload for /update_opportunity_key_details
    const keyDetailsPayload = {
      opportunity_id: opportunityId,
      user_id: userId,
      api_key: apiKey,
    };

    if (stage) keyDetailsPayload.stage = stage;
    if (solutionType) keyDetailsPayload.solution_type = solutionType;
    if (vendor) keyDetailsPayload.vendor = vendor;
    if (commit_date) keyDetailsPayload.commit_date = commit_date; // Include commit_date
    if (value) keyDetailsPayload.value = value; // Include value

    if (
      keyDetailsPayload.stage ||
      keyDetailsPayload.solution_type ||
      keyDetailsPayload.vendor ||
      keyDetailsPayload.commit_date || // Ensure commit_date is checked
      keyDetailsPayload.value
    ) {
      await axios8001.post("/update_opportunity_key_details", keyDetailsPayload);
    }

    console.log("Updated opportunity details successfully.");

    setOpportunityDetails((prev) => ({
      ...prev,
      contact: contact || prev.contact,
      champions: champions || prev.champions,
      stage: stage || prev.stage,
      solutionType: solutionType || prev.solutionType,
      vendor: vendor || prev.vendor,
      commit_date: commit_date || prev.commit_date, // Update frontend state
      value: value || prev.value,
    }));
    setEditMode(false);
  } catch (error) {
    console.error("Error saving opportunity details:", error);
  }
};

  
  // Fetch Opportunity Details
  // const fetchOpportunityDetails = useCallback(async () => {
  //   try {
  //     setLoading(true); // Start loading
  //     const response = await axios8001.get('/get_opportunity_details', {
  //       params: { opportunity_id: opportunityId },
  //       headers: { "x-user-id": userId, "x-api-key": apiKey },
  //     });
  //     if (response.data) {
  //       setOpportunityDetails(response.data);
  //       setStage(response.data.stage || '');
  //       setSolutionType(response.data.solutionType || '');
  //     } else {
  //       setError('Opportunity details not found.');
  //     }
  //   } catch (error) {
  //     setError('Error fetching opportunity details.');
  //   } finally {
  //     setLoading(false); // End loading
  //   }
  // }, [opportunityId, userId, apiKey]);

  // Fetch opportunity details on component load
  const fetchOpportunityDetails = useCallback(async () => {
    try {
      const userId = sessionStorage.getItem("user_id");
      const apiKey = sessionStorage.getItem("api_key");
  
      if (!userId || !apiKey) {
        console.error("Missing user ID or API Key.");
        return;
      }
  
      // Attempt to fetch full opportunity details
      const response = await axios8001.get("/get_opportunity_full_details", {
        params: { opportunity_id: opportunityId },
        headers: { "x-user-id": userId, "x-api-key": apiKey },
      });
  
      if (response.data) {
        const {
          customer_name,
          opportunity_name,
          stage,
          solution_type,
          vendor,
          champions,
          contacts,
          commit_date, // Add commit_date from response
          value,       // Add value from response
        } = response.data;
  
        setOpportunityDetails({
          customer_name,
          opportunity_name,
          stage: stage || "",
          solution_type: solution_type || "",
          vendor: vendor || "",
          champions: champions || [],
          contacts: contacts || [],
          commit_date: commit_date || null, // Handle commit_date
          value: value || null,             // Handle value
        });
      } else {
        console.error("No opportunity details found.");
      }
    } catch (error) {
      console.error("Error fetching opportunity details. Falling back to basic details...", error);
  
      // Fallback to basic details
      try {
        const basicResponse = await axios8001.get("/get_opportunity_details", {
          params: { opportunity_id: opportunityId },
          headers: { "x-user-id": userId, "x-api-key": apiKey },
        });
  
        if (basicResponse.data) {
          const { customer_name, opportunity_name } = basicResponse.data;
  
          setOpportunityDetails({
            customer_name,
            opportunity_name,
            stage: "",
            solution_type: "",
            vendor: "",
            champions: [],
            contacts: [],
            commit_date: null, // Fallback doesn't include commit_date
            value: null,       // Fallback doesn't include value
          });
        } else {
          console.error("No basic opportunity details found.");
        }
      } catch (fallbackError) {
        console.error("Error fetching basic opportunity details:", fallbackError);
      }
    }
  }, [opportunityId]);
  

  // const generateTasks = async () => {
  //   try {
  //     console.log('Generating tasks...');
  //     const response = await axios8200.post('/generate_tasks', {
  //       params: { opportunity_id: opportunityId },
  //     });
  //     console.log('Tasks generated successfully:', response.data);
  //     setTasks(response.data.tasks || []);
  //   } catch (error) {
  //     console.error('Error generating tasks:', error);
  //   }
  // };
  useEffect(() => {
    if (!opportunityId && urlOpportunityName) {
      console.warn("Attempting fallback for opportunity ID using customer name and opportunity name.");
      fetchOpportunityIdByName();
    } else if (!opportunityId) {
      console.error("Opportunity ID is still missing.");
    }
  }, [opportunityId, urlOpportunityName]);
  
const [avgDealTimeDays, setAvgDealTimeDays] = useState(90); // Default value for avgDealTimeDays
const [showGenerateTasksModal, setShowGenerateTasksModal] = useState(false);

const generateTasks = async (opportunityId, avgDealTimeDays = 90) => {
  if (!opportunityId) {
    console.error("Opportunity ID is missing for task generation.");
    alert("Opportunity ID is missing. Unable to generate tasks.");
    return;
  }
  try {
    console.log("Generating tasks...");
    // Construct the query string with parameters
    const response = await axios8200.post(
      `/generate_tasks/?opportunity_id=${encodeURIComponent(opportunityId)}&avg_deal_time_days=${encodeURIComponent(avgDealTimeDays)}`
    );
    console.log("Tasks generated successfully:", response.data);
    setTasks(response.data.tasks || []);
  } catch (error) {
    console.error("Error generating tasks:", error);
    alert("Failed to generate tasks. Please try again.");
  }
};


/* This is analyse tasks modal form and button on navbar code */

const [showAnalyzeTaskModal, setShowAnalyzeTaskModal] = useState(false);
const [email, setEmail] = useState(""); // Email state managed in parent

const fetchEmail = async () => {
  try {
    const response = await axios8001.get("/get_user_details", {
      params: { api_key: apiKey, user_id: userId },
    });

    if (response.data && response.data.status === "success") {
      setEmail(response.data.email); // Store email in state
    } else {
      console.error("Failed to fetch email:", response.data.message || "Unknown error");
      alert("Failed to fetch email. Please ensure user_id and api_key are correct.");
    }
  } catch (error) {
    console.error("Error fetching email:", error);
    alert("An error occurred while fetching the email.");
  }
};

const openAnalyzeTaskModal = async () => {
  try {
    setShowAnalyzeTaskModal(true); // Open modal first
    await fetchEmail(); // Then fetch email
  } catch (error) {
    console.error("Error fetching email:", error);
    alert("Failed to fetch email. You can retry inside the modal.");
  }
};



const AnalyzeTaskModal = ({ isOpen, onClose, opportunityId, email }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAnalyzeTasks = async () => {
    console.log("AnalyzeTaskModal: handleAnalyzeTasks invoked"); // Add this log
    console.log("Opportunity ID:", opportunityId);
    console.log("Email:", email);

    if (!opportunityId || !email) {
      alert("Opportunity ID or Email is missing!");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios8200.post("/analyze_and_adjust_tasks/", null, {
        params: {
          opportunity_id: opportunityId,
          email: email,
        },
      });

      if (response.data && response.data.status === "success") {
        setResult(response.data.suggestions || "No suggestions found.");
      } else {
        setResult(response.data.message || "No updates available.");
      }
    } catch (error) {
      console.error("Error analyzing tasks:", error);
      setResult("An error occurred while analyzing tasks.");
    } finally {
      setLoading(false);
    }
  };

  // Automatically trigger task analysis when the modal is opened
  useEffect(() => {
    if (isOpen) {
      handleAnalyzeTasks();
    }
  }, [isOpen]); // Run when `isOpen` changes

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content">
        <h2>Analyze and Adjust Tasks</h2>

        {/* Display loading, result, or default message */}
        {loading ? (
          <p>Analyzing tasks...</p>
        ) : result ? (
          <div>
            <h4>Analysis Results</h4>
            <pre>{JSON.stringify(result, null, 2)}</pre>
          </div>
        ) : (
          <p>No analysis available. Please wait or close the modal.</p>
        )}

        {/* Close button only */}
        <div className="modal-buttons">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};


const [result, setResult] = useState(null);

const handleAnalyzeTasks = async () => {
  if (!opportunityId || !email) {
    alert("Opportunity ID or Email is missing! Standalone");
    return;
  }

  setLoading(true);
  setResult(null);

  try {
    const response = await axios8200.post("/analyze_and_adjust_tasks/", null, {
      params: {
        opportunity_id: opportunityId,
        email: email,
      },
    });

    if (response.data && response.data.status === "success") {
      setResult(response.data.suggestions || "No suggestions found.");
    } else {
      setResult(response.data.message || "No updates available.");
    }
  } catch (error) {
    console.error("Error analyzing tasks:", error);
    setResult("An error occurred while analyzing tasks.");
  } finally {
    setLoading(false);
  }
};


  
  // const generateTasks = async (opportunityId, avgDealTimeDays = 90) => {
  //   try {
  //     console.log("Generating tasks...");
  //     // Ensure both parameters are sent in the request
  //     const response = await axios8200.post('/generate_tasks/', {
  //       opportunity_id: opportunityId,
  //       avg_deal_time_days: avgDealTimeDays,
  //     });
  
  //     console.log("Tasks generated successfully:", response.data);
  //     setTasks(response.data.tasks || []);
  //   } catch (error) {
  //     console.error("Error generating tasks:", error);
  
  //     // Handle specific error cases
  //     if (error.response?.status === 422) {
  //       alert("Invalid request. Ensure all required parameters are provided.");
  //     } else {
  //       alert("Failed to generate tasks. Please try again.");
  //     }
  //   }
  // };
  
  
  // Function to fetch tasks based on search criteria
const searchTasksWithFilters = async (searchParams) => {
  try {
    console.log("Original search params:", searchParams);

    // Filter out empty or undefined values from searchParams
    const filteredParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
      if (value !== "" && value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    console.log("Filtered search params:", filteredParams);

    // Convert filteredParams to query string
    const queryString = new URLSearchParams(filteredParams).toString();

    // Use GET method with query parameters
    const response = await axios8200.post(`/search_tasks/?${queryString}`);
    console.log("Tasks fetched successfully:", response.data.tasks);

    setTasks(response.data.tasks || []); // Populate tasks
    setSearchModalVisible(false); // Close modal
  } catch (error) {
    console.error("Error searching tasks:", error);
    alert("Search failed. Please try again.");
  } finally {
    setIsSearching(false); // Reset the loading state
  }
};

// Function to fetch opportunity next steps
const fetchOpportunityNextSteps = async () => {
  setLoadingNextSteps(true);
  setErrorNextSteps(null);

  try {
    const response = await axios8200.get(`/suggested_next_steps/${opportunityId}`);
    setNextSteps(response.data?.next_steps || []);
  } catch (error) {
    console.error('Error fetching Opportunity Next Steps:', error);
    setErrorNextSteps('Failed to fetch Opportunity Next Steps.');
  } finally {
    setLoadingNextSteps(false);
  }
};

  // Fetch Opportunity ID by Name
  const fetchOpportunityIdByName = useCallback(async () => {
    console.log('Fetching Opportunity ID by Name...');
    try {
      setLoading(true);
      const response = await axios8001.get('/get_opportunity_id', {
        params: {
          customer_id: customerIdFromUrl,
          opportunity_name: urlOpportunityName,
        },
      });
      if (response.data?.opportunity_id) {
        setOpportunityId(response.data.opportunity_id);
        console.log('Fetched Opportunity ID:', response.data.opportunity_id);
      } else {
        console.warn('Opportunity ID not found.');
      }
    } catch (error) {
      console.error('Error fetching Opportunity ID:', error);
      setError('Failed to fetch Opportunity ID.');
    } finally {
      setLoading(false);
    }
  }, [customerIdFromUrl, urlOpportunityName]);

  
  const fetchSuggestedNextSteps = useCallback(async () => {
    console.log('Fetching Suggested Next Steps...');
    try {
      const response = await axios8200.get(`/suggested_next_steps/${opportunityId}`);
      const steps = Object.entries(response.data.next_steps || {}).flatMap(([role, steps]) =>
        steps.map((step) => `${role}: ${step}`)
      );
      setSuggestedNextSteps(steps);
      console.log('Fetched Suggested Next Steps:', steps);
    } catch (error) {
      console.error('Error fetching Suggested Next Steps:', error);
    }
  }, [opportunityId]);

// Function to view, add updates and save task changes
// const fetchViewAndEditTasks = useCallback(async () => {
//   console.log("Fetching View and Edit Tasks...");
//   try {
//       const response = await axios8200.post("/view_and_edit_tasks/", {
//           opportunity_id: opportunityId,
//           api_key: apiKey,
//           user_id: userId,
//       });
//       console.log("Fetched Tasks:", response.data.tasks);
//       // Map task names if missing
//       const mappedTasks = response.data.tasks.map((task, index) => ({
//           ...task,
//           name: task.name || `Task ${index + 1}`, // Add default name if missing
//       }));
//       setTasks(mappedTasks);
//   } catch (error) {
//       console.error("Error fetching View and Edit Tasks:", error);
//   }
// }, [opportunityId, apiKey, userId]);


  // 1. Fetch Tasks Function
const fetchViewAndEditTasks = useCallback(async () => {
  if (!opportunityId) {
    console.error("Opportunity ID is missing for fetching tasks.");
    setTasks(["Opportunity ID is missing."]);
    return;
  }

  try {
    const response = await axios8200.post("/view_and_edit_tasks/", {
      opportunity_id: opportunityId,
      api_key: apiKey,
      user_id: userId,
    });

    const tasks = response.data.tasks || [];
    // Ensure tasks are always objects, even if no tasks exist
    setTasks(
      tasks.length > 0
        ? tasks
        : [{ task_name: "No tasks available.", randomColor: null }] // Fallback task structure
    );
    console.log("Fetched tasks:", tasks);
  } catch (error) {
    console.error("Error fetching View and Edit Tasks:", error);
    // Ensure a consistent structure in case of an error
    setTasks([{ task_name: "Error fetching tasks.", randomColor: null }]);
  }
}, [opportunityId, apiKey, userId]);
  
  // Function to save task changes
  const saveTaskChanges = async (updatedTask) => {
    if (!updatedTask || typeof updatedTask !== "object") {
        console.error("Invalid task structure:", updatedTask);
        alert("Cannot save task. Task structure is invalid.");
        return;
    }

    console.log("Saving Task Changes:", updatedTask);

    try {
        const payload = {
            opportunity_id: opportunityId,
            api_key: apiKey,
            user_id: userId,
            updates: [
                {
                    task_id: updatedTask.task_id,
                    task_name: updatedTask.name || "",
                    status: updatedTask.status || "",
                    description: updatedTask.description || "", // Include updated description
                    assigned_to: updatedTask.assigned_to || "", // Include assigned_to field
                    execution_update: updatedTask.new_execution_update || null,
                },
            ],
        };

        // Remove the `execution_update` key if it is `null`
        if (!updatedTask.new_execution_update) {
            delete payload.updates[0].execution_update;
        }

        const response = await axios8200.post("/view_and_edit_tasks/", payload);
        console.log("Task saved successfully:", response.data);

        // Update the local task list
        setTasks((prev) =>
            prev.map((task) => (task.task_id === updatedTask.task_id ? updatedTask : task))
        );
        closeModal();
    } catch (error) {
        console.error("Error saving task changes:", error);
    }
};

  
  
  // Function to add an execution update
  const addExecutionUpdate = () => {
    const timestamp = new Date().toISOString();
    const newExecutionUpdate = {
        update: newUpdate,
        user_id: userId,
        timestamp,
    };

    setActiveTask((prev) => ({
        ...prev,
        new_execution_update: newUpdate, // Bind to the backend payload
        execution_updates: [...(prev.execution_updates || []), newExecutionUpdate],
    }));

    setNewUpdate(""); // Clear the input field
};

  
  // Effect to initialize the active task
  useEffect(() => {
    if (!activeTask) {
        setActiveTask({
            name: "", // Default empty name
            status: "Started", // Default status
            description: "", // Default empty description
            assigned_to: "", // Default empty assigned_to (or set a default role if required)
            execution_updates: [], // Default empty updates
        });
    }
}, [activeTask]);

  
  const handleInputChange = (field) => (e) => {
    setActiveTask((prev) => ({ ...prev, [field]: e.target.value || "" }));
  };
  

  const handleNewUpdateChange = (e) => {
    setNewUpdate(e.target.value);
};

const fetchNextSteps = async (opportunityId) => {
  setLoadingNextSteps(true);
  setErrorNextSteps(null);

  try {
    // Dynamically include opportunity_id in the API endpoint
    const response = await axios8200.get(`/suggested_next_steps/${opportunityId}`);

    // Update state with the fetched next steps
    setNextSteps(response.data.steps || []);
  } catch (error) {
    console.error("Error fetching next steps:", error); // Log error details for debugging

    // Set a more descriptive error message
    setErrorNextSteps(
      error.response?.data?.message || "Failed to load next steps. Please try again."
    );
  } finally {
    setLoadingNextSteps(false);
  }
};

// useEffect(() => {
//   if (!opportunityId) {
//     console.error("Opportunity ID is missing. Skipping API calls.");
//     setLearnings(["Opportunity ID is missing."]);
//     setSuggestedTasks(["Opportunity ID is missing."]);
//     return;
//   }

//   fetchSuggestedTasks();
//   fetchLearnings();
// }, [opportunityId, fetchLearnings, fetchSuggestedTasks]); // All dependencies included


const openNextStepsModal = async () => {
  setNextStepsModalVisible(true);
  await fetchNextSteps(opportunityId); // Pass the required opportunityId
};




// const fetchLearnings = useCallback(async () => {
//   if (!opportunityId) {
//     console.error("Opportunity ID is missing for fetchLearnings.");
//     setLearnings(["Opportunity ID is missing."]);
//     return;
//   }

//   try {
//     const response = await axios8200.get(`/opportunity_learnings/${opportunityId}`);
//     setLearnings(response.data.learnings || []);
//   } catch (error) {
//     if (error.response && error.response.status === 404) {
//       console.error(`Learnings not found for opportunity ID: ${opportunityId}`);
//       setLearnings(["No learnings found for this opportunity."]);
//     } else {
//       console.error("Error fetching learnings:", error);
//       setLearnings(["Error fetching learnings."]);
//     }
//   }
// }, [opportunityId]);

// 2. Fetch Learnings Function
const fetchLearnings = useCallback(async () => {
  if (!opportunityId) {
    console.error("Opportunity ID is missing for fetching learnings.");
    setLearnings(["Opportunity ID is missing."]);
    return;
  }

  try {
    const response = await axios8200.get(`/opportunity_learnings/${opportunityId}`);
    const learnings = response.data.learnings || [];
    setLearnings(learnings.length > 0 ? learnings : ["No learnings available."]);
    console.log("Fetched learnings:", learnings);
  } catch (error) {
    console.error("Error fetching learnings:", error);
    setLearnings(["Error fetching learnings."]);
  }
}, [opportunityId]);


const fetchSuggestedTasks = useCallback(async () => {
  if (!opportunityId) {
    console.error("Opportunity ID is missing for fetchSuggestedTasks.");
    setSuggestedTasks(["Opportunity ID is missing."]);
    return;
  }

  try {
    const response = await axios8200.get("/fetch_suggested_tasks", {
      params: { opportunity_id: opportunityId },
    });
    setSuggestedTasks(response.data.tasks || ["No tasks available."]);
  } catch (error) {
    console.error("Error fetching tasks:", error);
    setSuggestedTasks(["Error fetching tasks."]);
  }
}, [opportunityId]);





  /* Dashboard Metric */

  


  const fetchSalesStageHealth = useCallback(async () => {
    if (!opportunityId) {
      console.error("Opportunity ID is not defined for fetchSalesStageHealth");
      return;
    }
  
    try {
      const response = await axios8200.get(`/sales_stage_health/${opportunityId}`);
      if (response.data) {
        setMetrics((prevMetrics) => ({
          ...prevMetrics,
          salesStageHealth: {
            stage: response.data.sales_stage || "N/A",
            meddiccHealth: response.data.Meddicc_health_score || 0,
            complianceScore: response.data.compliance_score || 0,
          },
        }));
        console.log("Fetched Sales Stage Health successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching Sales Stage Health:", error);
    }
  }, [opportunityId]);
  
  const fetchClosingProbability = useCallback(async () => {
    if (!opportunityId) {
      console.error("Opportunity ID is undefined for fetchClosingProbability");
      return; // Exit if opportunityId is not set
    }
  
    try {
      const response = await axios8200.get(`/closing_probability/${opportunityId}`);
      if (response.data) {
        setMetrics((prevMetrics) => ({
          ...prevMetrics,
          closingProbability: response.data.closing_probability || "N/A",
        }));
        console.log("Fetched Closing Probability successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching Closing Probability:", error);
    }
  }, [opportunityId]);
  
  
  // const fetchClosingProbability = async (opportunityId) => {
  //   console.log("Fetching Closing Probability...");
  //   try {
  //     const response = await axios8200.get(`/closing_probability/${opportunityId}`);
  //     console.log("Closing Probability Response:", response.data);
  //     setMetrics((prev) => ({
  //       ...prev,
  //       closingProbability: response.data.closing_probability * 100, // Convert to percentage
  //     }));
  //   } catch (error) {
  //     console.error("Error fetching Closing Probability:", error);
  //   }
  // };
  
  const fetchTaskMetrics = useCallback(async () => {
    if (!opportunityId) {
      console.error("Opportunity ID is not defined for fetchTaskMetrics");
      return;
    }
  
    try {
      const response = await axios8200.get(`/task_metrics/${opportunityId}`);
      if (response.data) {
        setMetrics((prevMetrics) => ({
          ...prevMetrics,
          taskMetrics: {
            deletedTasksCount: response.data.deleted_tasks_count || 0,
            totalTasks: response.data.total_tasks || 0,
          },
        }));
        console.log("Fetched Task Metrics successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching Task Metrics:", error);
    }
  }, [opportunityId]);
  
  const fetchTasksSummary = useCallback(async () => {
    if (!opportunityId) {
      console.error("Opportunity ID is not defined for fetchTasksSummary");
      return;
    }
  
    try {
      const response = await axios8200.get(`/tasks_summary/${opportunityId}`);
      if (response.data) {
        setMetrics((prevMetrics) => ({
          ...prevMetrics,
          tasksSummary: {
            total_tasks: response.data.total_tasks || 0,
            completed_tasks: response.data.completed_tasks || 0,
            pending_tasks: response.data.pending_tasks || 0,
            delayed_tasks: response.data.overdue_tasks || 0, // Assuming "overdue_tasks" maps to delayed tasks
          },
        }));
        console.log("Fetched Tasks Summary successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching Tasks Summary:", error);
    }
  }, [opportunityId]);
      

  // const fetchDashboardMetrics = useCallback(async () => {
  //   console.log("Fetching Dashboard Metrics...");
  //   try {
  //     await Promise.all([
  //       fetchSalesStageHealth(),
  //       fetchTaskMetrics(),
  //       fetchClosingProbability(),
  //       fetchTasksSummary(), // Include this if it fetches useful data
  //     ]);
  //     console.log("Fetched all Dashboard Metrics successfully.");
  //   } catch (error) {
  //     console.error("Error fetching Dashboard Metrics:", error);
  //   }
  // }, [
  //   fetchSalesStageHealth,
  //   fetchTaskMetrics,
  //   fetchClosingProbability,
  //   fetchTasksSummary,
  // ]);
  

  const fetchDashboardMetrics = useCallback(async () => {
    if (!opportunityId) {
      console.error("Opportunity ID is not defined");
      return;
    }
  
    console.log("Fetching Dashboard Metrics for Opportunity ID:", opportunityId);
  
    try {
      await Promise.all([
        fetchSalesStageHealth(),
        fetchTaskMetrics(),
        fetchClosingProbability(),
        fetchTasksSummary(),
      ]);
      console.log("Fetched all Dashboard Metrics successfully.");
    } catch (error) {
      console.error("Error fetching Dashboard Metrics:", error);
    }
  }, [opportunityId, fetchSalesStageHealth, fetchTaskMetrics, fetchClosingProbability, fetchTasksSummary]);
  
 

  const closeModal = () => {
    console.log('Closing Modal...');
    setModalVisible(false);
    setActiveTask(null);
  };
/* This is the function which fethces attributes of the tasks */
const handleTaskDoubleClick = (task) => {
  console.log('Task Double Clicked:', task);
  setActiveTask({
      task_id: task.task_id || "", // Include task_id
      task_name: task.task_name || "",
      description: task.description || "",
      status: task.status || "Not Started",
      execution_updates: task.execution_update || [],
      stage: task.stage || "",
      end_date: task.end_date || "",
      start_date: task.start_date || "",
      assigned_to: task.assigned_to || "", // Include assigned_to, default to an empty string if missing
  });

  setModalVisible(true);
};

  
  useEffect(() => {
    if (!opportunityId && urlCustomerName && urlOpportunityName) {
      fetchOpportunityIdByName();
    }
  }, [opportunityId, urlCustomerName, urlOpportunityName, fetchOpportunityIdByName]);

  useEffect(() => {
    if (opportunityId) {
      fetchOpportunityDetails();
    }
  }, [opportunityId, fetchOpportunityDetails]);

  useEffect(() => {
    if (opportunityId) {
      fetchDashboardMetrics();
    } else {
      console.error("Opportunity ID is not set in useEffect");
    }
  }, [opportunityId, fetchDashboardMetrics]);
  

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
  
    // Add user's message to the chat
    const userMessage = {
      content: inputValue,
      role: "user",
      timestamp: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setLoadingChat(true);
  
    try {
      // Retrieve User ID and API Key
      const userId = sessionStorage.getItem("user_id");
      const apiKey = sessionStorage.getItem("api_key");
  
      if (!userId || !apiKey) {
        console.error("Missing user ID or API key.");
        setChatMessages((prev) => [
          ...prev,
          {
            role: "error",
            content: "Error: Missing user ID or API key. Please log in again.",
          },
        ]);
        return;
      }
  
      // Prepare the payload for the API request
      const payload = {
        question: inputValue,
        opportunity_id: opportunityId || undefined,
        stage: stage || undefined,
        user_id: userId, // Add user_id
        api_key: apiKey, // Add api_key
      };
  
      // Log payload for debugging
      console.log("Sending payload:", payload);
  
      // Send the request to the /ask endpoint
      const response = await axios8000.post("/ask", payload);
  
      // Log response for debugging
      console.log("API Response:", response.data);
  
      // Handle assistant's response
      if (response.data?.status === "success") {
        const assistantResponse = response.data?.data?.response; // Extract response field
        if (assistantResponse) {
          setChatMessages((prev) => [
            ...prev,
            {
              content: assistantResponse,
              role: "assistant",
              timestamp: new Date().toISOString(),
            },
          ]);
        } else {
          throw new Error("No valid response received from assistant.");
        }
      } else {
        throw new Error("Response status is not success.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setChatMessages((prev) => [
        ...prev,
        { content: "An error occurred. Please try again.", role: "error" },
      ]);
    } finally {
      setLoadingChat(false);
    }
  };
  
  // Function to format assistant response
  const renderFormattedResponse = (content) => {
    // Split response into sections based on '###' headings
    const sections = content.split("###").filter((section) => section.trim());
  
    return (
      <div className="assistant-response">
        {sections.map((section, index) => {
          const [heading, ...paragraphs] = section.split("\n").map((line) => line.trim());
          return (
            <div key={index} className="response-section">
              <h4 className="response-heading">{heading}</h4>
              <div className="response-content">
                {paragraphs.map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  
  
  
  // Handle sending a message when opportunityId changes (e.g., on page load or refresh)
  useEffect(() => {
    if (opportunityId) {
      handleSendMessage();
    }
  }, [opportunityId]);

  useEffect(() => {
    if (opportunityId) fetchOpportunityDetails();
  }, [opportunityId, fetchOpportunityDetails]);

  
  
  useEffect(() => {
    if (!opportunityId && urlCustomerName && urlOpportunityName) {
      fetchOpportunityIdByName();
    }
  }, [opportunityId, urlCustomerName, urlOpportunityName]);
  
  
  // 3. UseEffect Hook to Trigger Fetch Functions
useEffect(() => {
  if (opportunityId) {
    fetchViewAndEditTasks();
    fetchLearnings();
  } else {
    console.error("Opportunity ID is missing. Skipping tasks and learnings fetch.");
  }
}, [opportunityId, fetchViewAndEditTasks, fetchLearnings]);
  
useEffect(() => {
  const chatContainer = document.querySelector(".chat-container");
  if (chatContainer) {
    chatContainer.scrollTop = chatContainer.scrollHeight;
  }
}, [chatMessages]);

// Call fetchEmail when the component mounts
useEffect(() => {
  fetchEmail();
}, []);

return (
  <div className="opportunity-page">
    {/* Opportunity Navbar */}
    <OpportunityNavbar
  setShowAskCoachModal={setShowAskCoachModal}
  setSearchModalVisible={setSearchModalVisible}
  setNextStepsModalVisible={setNextStepsModalVisible}
  setShowAnalyzeTaskModal={setShowAnalyzeTaskModal}
  setCreateTaskModalVisible={setCreateTaskModalVisible} // Pass the setter function here
  generateTasks={() => generateTasks(opportunityId)} // Pass opportunityId here
  fetchOpportunityNextSteps={fetchOpportunityNextSteps}
  setShowUpdateMeddiccModal={setShowUpdateMeddiccModal}
  
/>


{showAnalyzeTaskModal && (
  <div className="modal analyze-task-modal">
    <AnalyzeTaskModal
      isOpen={showAnalyzeTaskModal}
      onClose={() => setShowAnalyzeTaskModal(false)}
      opportunityId={opportunityId}
      email={email || "default@example.com"} // Provide a fallback for testing
      handleAnalyzeTasks={handleAnalyzeTasks}
    />
  </div>
)}


{/* Ask Coach Modal */}
{showAskCoachModal && (
      <div className="ask-coach-modal">
        <div className="modal-content">
          <h2>Ask Coach</h2>
          <button
            onClick={() => setShowAskCoachModal(false)}
            className="close-button"
          >
            ✕
          </button>
          <div className="ask-coach-body">
            {Object.keys(contexts).map((buttonKey) => (
              <button
                key={buttonKey}
                className="coach-button"
                onClick={() => handleSendMessageWithContext(buttonKey)}
              >
                {buttonKey}
              </button>
            ))}
       
  
      </div>




    
    
          {/* Chat and Input */}
 {/* Chat Container */}
<div className="chat-container">
  {chatMessages.map((message, index) => (
    <div key={index} className={`chat-message ${message.role}`}>
      {/* Render user messages */}
      {message.role === "user" ? (
        <div className="user-message">
          <p><strong>You:</strong> {message.content}</p>
        </div>
      ) : (
        // Render assistant messages
        message.role === "assistant" ? (
          renderFormattedResponse(message.content)
        ) : (
          // Render error or other roles
          <p>{message.content}</p>
        )
      )}
    </div>
  ))}

  {/* Loading State */}
  {loadingChat && (
    <div className="chat-loading">
      <p><strong>Processing your request...</strong></p>
    </div>
  )}
</div>

{/* Input Section */}
<div className="chat-input">
  <input
    type="text"
    placeholder="Type your question..."
    value={inputValue}
    onChange={(e) => setInputValue(e.target.value)}
  />
  <button onClick={handleSendMessage}>Send</button>
</div>

        </div>
      </div>
    )}


  <div className="main-content">
    {/* Opportunity Details and Other Sections */}
  </div>

  {/* Top Section */}
  <div className="top-section-grid">
    {/* Opportunity Details Tile */}
<div className="opportunity-details-tile" style={{ gridArea: "details", flex: "1" }}>
  <h3>Opportunity Details</h3>
  {!editMode ? (
    <OpportunityDetailsView
      details={opportunityDetails}
      customerId={customerId}
      opportunityId={opportunityId}
      onEditClick={() => setEditMode(true)}
    />
  ) : (
    <OpportunityDetailsForm
      opportunityDetails={opportunityDetails} // Pass entire object directly
      onSave={saveUpdatedDetails}
      onCancel={() => setEditMode(false)}
    />
  )}
</div>


    {/* Dashboard Section */}
<div className="dashboard-grid">
  {/* Total Tasks */}
  <div className="dashboard-tile number-tile">
    <h4>Total Tasks</h4>
    <p className="large-number">{metrics.tasksSummary?.total_tasks ?? "N/A"}</p>
  </div>

  {/* Completed Tasks */}
  <div className="dashboard-tile number-tile">
    <h4>Completed Tasks</h4>
    <p className="large-number">{metrics.tasksSummary?.completed_tasks ?? "N/A"}</p>
  </div>

  {/* Pending Tasks */}
  <div className="dashboard-tile number-tile">
    <h4>Pending Tasks</h4>
    <p className="large-number">{metrics.tasksSummary?.pending_tasks ?? "N/A"}</p>
  </div>

  {/* Delayed Tasks */}
  <div className="dashboard-tile number-tile">
    <h4>Delayed Tasks</h4>
    <p className="large-number">{metrics.tasksSummary?.delayed_tasks ?? "N/A"}</p>
  </div>

  {/* Sales Stage Health */}
  <div className="dashboard-tile chart-tile">
    <h4>Sales Stage Health</h4>
    {metrics.salesStageHealth ? (
      <div>
        <p><strong>Stage:</strong> {metrics.salesStageHealth.stage || "N/A"}</p>
        <p><strong>Meddicc Health:</strong> {metrics.salesStageHealth.meddiccHealth ?? 0}</p>
        <p><strong>Compliance:</strong> {metrics.salesStageHealth.complianceScore ?? 0}</p>
      </div>
    ) : (
      <p>No data available.</p>
    )}
  </div>

  {/* Closing Probability */}
  <div className="dashboard-tile closing-probability-tile">
    <h4>Closing Probability</h4> {/* Heading defined only in the parent */}
    <ClosingProbabilityTile closingProbability={metrics.closingProbability * 100} />
</div>

  {/* Task Status */}
  <div className="dashboard-tile chart-tile">
    <h4>Task Status</h4>
    {metrics.tasksSummary ? (
      <CustomBarChart
        data={[
          { name: "Total", value: metrics.tasksSummary?.total_tasks || 0 },
          { name: "Completed", value: metrics.tasksSummary?.completed_tasks || 0 },
          { name: "Pending", value: metrics.tasksSummary?.pending_tasks || 0 },
          { name: "Delayed", value: metrics.tasksSummary?.delayed_tasks || 0 },
        ]}
      />
    ) : (
      <p>No data available.</p>
    )}
  </div>

      {/* Tasks Section */}
<div className="tasks-section">
  <h4>Tasks</h4>
  {tasks.length > 0 ? (
    <ul>
      {tasks.slice(0, 10).map((task, index) => {
        const taskClass =
          task.role === "Sales Executive"
            ? "task-sales-exec"
            : task.role === "Sales Engineer"
            ? "task-sales-eng"
            : task.role === "Customer Success Manager"
            ? "task-csm"
            : "";

        const randomColor =
          task.randomColor || `#${Math.floor(Math.random() * 16777215).toString(16)}`;
        task.randomColor = randomColor; // Save the random color in task

        return (
          <li
            key={task.task_id || index}
            className={`task-item ${taskClass}`}
            style={{ backgroundColor: randomColor }}
            onDoubleClick={() => {
              console.log("Task double-clicked:", task);
              handleTaskDoubleClick(task);
            }}
          >
            {task.task_name || `Task ${index + 1}`} {/* Use task_name explicitly */}
          </li>
        );
      })}
    </ul>
  ) : (
    <p>No tasks available.</p> // Updated fallback for when no tasks exist
  )}
</div>

 


   </div>
  
   <div className="learnings-section-container">
  <div className="learnings-section">
    <h4>Learnings</h4>
    {learnings.length > 0 ? (
      <ul>
        {learnings.map((learning, index) => (
          <li key={index}>{learning}</li>
        ))}
      </ul>
    ) : (
      <p>No learnings available.</p>
    )}
  </div>



</div>
</div>;

       {/* Search Modal */}
{searchModalVisible && (
  <div className="modal">
    <div className="modal-content">
      {isSearching ? (
        <p style={{ textAlign: "center" }}>Searching...</p>
      ) : (
        <>
          <h3 style={{ textAlign: "center" }}>Search Tasks</h3>
          <form
            onSubmit={async (e) => {
              e.preventDefault();

              // Collect form data
              const formData = new FormData(e.target);
              const searchParams = Object.fromEntries(formData.entries());

              // Ensure `opportunity_id` is included
              if (!searchParams.opportunity_id) {
                searchParams.opportunity_id = opportunityId;
              }

              setIsSearching(true);

              try {
                const tasks = await searchTasksWithFilters(searchParams);

                if (tasks?.length > 0) {
                  console.log("Search Results:", tasks);
                  setSearchResults(tasks);
                } else {
                  console.warn("No tasks found for the given criteria.");
                  setSearchResults([]);
                }
              } catch (error) {
                console.error("Error fetching tasks:", error);
                setSearchResults([]);
              } finally {
                setIsSearching(false);
              }
            }}
          >
            <input type="hidden" name="opportunity_id" value={opportunityId} />

            {/* Form inputs for search */}
            <label htmlFor="assigned_to">Assigned To:</label>
            <input
              type="text"
              id="assigned_to"
              name="assigned_to"
              placeholder="Enter assigned user"
            />

            <label htmlFor="status">Status:</label>
            <input
              type="text"
              id="status"
              name="status"
              placeholder="Enter task status"
            />

            <label htmlFor="stage">Stage:</label>
            <input
              type="text"
              id="stage"
              name="stage"
              placeholder="Enter stage"
            />

            <label htmlFor="start_day">Start Day:</label>
            <input
              type="number"
              id="start_day"
              name="start_day"
              placeholder="Enter start day (1-31)"
              min="1"
              max="31"
            />

            <label htmlFor="start_month">Start Month:</label>
            <input
              type="number"
              id="start_month"
              name="start_month"
              placeholder="Enter start month (1-12)"
              min="1"
              max="12"
            />

            <label htmlFor="start_year">Start Year:</label>
            <input
              type="number"
              id="start_year"
              name="start_year"
              placeholder="Enter start year"
            />

            <label htmlFor="end_day">End Day:</label>
            <input
              type="number"
              id="end_day"
              name="end_day"
              placeholder="Enter end day (1-31)"
              min="1"
              max="31"
            />

            <label htmlFor="end_month">End Month:</label>
            <input
              type="number"
              id="end_month"
              name="end_month"
              placeholder="Enter end month (1-12)"
              min="1"
              max="12"
            />

            <label htmlFor="end_year">End Year:</label>
            <input
              type="number"
              id="end_year"
              name="end_year"
              placeholder="Enter end year"
            />

            <div className="form-buttons">
              <button type="submit" className="save-button">
                Search
              </button>
              <button
                type="button"
                className="cancel-button"
                onClick={() => {
                  setSearchModalVisible(false);
                  setSearchResults([]);
                }}
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Display Results */}
          {searchResults?.length > 0 ? (
            <div className="search-results">
              <h4>Search Results:</h4>
              <ul>
                {searchResults.map((task) => (
                  <li key={task.id}>
                    <strong>{task.task_name || "Unnamed Task"}</strong>
                    <p>{task.description || "No description provided."}</p>
                    <p>
                      <strong>Stage:</strong> {task.stage || "N/A"} |{" "}
                      <strong>Status:</strong> {task.status || "N/A"}
                    </p>
                    <p>
                      <strong>Start Date:</strong>{" "}
                      {task.start_date
                        ? new Date(task.start_date).toLocaleDateString()
                        : "N/A"}{" "}
                      | <strong>End Date:</strong>{" "}
                      {task.end_date
                        ? new Date(task.end_date).toLocaleDateString()
                        : "N/A"}
                    </p>
                    {/* Display execution updates */}
                    {task.execution_update?.length > 0 && (
                      <details>
                        <summary>Execution Updates</summary>
                        <ul>
                          {task.execution_update.map((update, idx) => (
                            <li key={idx}>
                              <strong>{update.timestamp}:</strong> {update.update}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            !isSearching && <p style={{ textAlign: "center" }}>No results found.</p>
          )}
        </>
      )}
    </div>
  </div>
)}




{/* Edit Task Modal */}
{modalVisible && activeTask && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit Task</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveTaskChanges(activeTask);
              }}
            >
              {/* Task Name */}
              <label htmlFor="task-name">Name:</label>
              <input
                id="task-name"
                type="text"
                value={activeTask?.name || ""}
                onChange={handleInputChange("name")}
              />

              {/* Task Description */}
              <label htmlFor="task-description">Description:</label>
              <textarea
                id="task-description"
                value={activeTask?.description || ""}
                onChange={handleInputChange("description")}
              ></textarea>

              {/* Assigned To */}
              <label htmlFor="task-assigned-to">Assigned To:</label>
              <select
                id="task-assigned-to"
                value={activeTask?.assigned_to || ""}
                onChange={handleInputChange("assigned_to")}
              >
                <option value="" disabled>
                  Select Role
                </option>
                <option value="Sales Executive">Sales Executive</option>
                <option value="Sales Engineer">Sales Engineer</option>
                <option value="Customer Success Manager">Customer Success Manager</option>
              </select>

              {/* Task Status */}
              <label htmlFor="task-status">Status:</label>
              <select
                id="task-status"
                value={activeTask?.status || ""}
                onChange={handleInputChange("status")}
              >
                <option value="Not Started">Not Started</option>
                <option value="Started">Started</option>
                <option value="Completed">Completed</option>
                <option value="Blocked">Blocked</option>
              </select>

              {/* Execution Updates */}
              <h4 className="execution-updates-label">Execution Updates</h4>
              <ul>
                {activeTask.execution_updates?.map((update, idx) => (
                  <li key={idx}>
                    <strong>{update.timestamp}</strong>: {update.update} ({update.user_id})
                  </li>
                ))}
              </ul>

              {/* Add New Update */}
              <label htmlFor="new-update">Add Update:</label>
              <textarea
                id="new-update"
                placeholder="Type your execution update..."
                value={newUpdate}
                onChange={handleNewUpdateChange}
              ></textarea>
              <button
                type="button"
                className="add-update-button"
                onClick={addExecutionUpdate}
              >
                Add Update
              </button>

              {/* Modal Buttons */}
              <div className="modal-buttons">
                <button type="submit" className="save-button">Save</button>
                <button type="button" className="cancel-button" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Opportunity Next Steps Modal */}
        {nextStepsModalVisible && (
      <div className="modal">
        <div className="modal-content">
          <h3>Opportunity Next Steps</h3>

          {/* Loading State */}
          {loadingNextSteps && <p>Loading...</p>}

          {/* Error State */}
          {errorNextSteps && <p className="error-message">{errorNextSteps}</p>}

          {/* Next Steps List */}
          {nextSteps && Object.keys(nextSteps).length > 0 ? (
            <div>
              {Object.entries(nextSteps).map(([role, steps]) => (
                <div key={role} className="next-steps-role-section">
                  <h4>{role}</h4>
                  <ul>
                    {steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            !loadingNextSteps && <p>No next steps available.</p>
          )}

          <button
            onClick={() => setNextStepsModalVisible(false)}
            className="close-button"
          >
            Close
          </button>
        </div>
        </div>
        )}

      {/* Update MEDDICC Modal */}
      {showUpdateMeddiccModal && (
        <div className="modal update-meddicc-modal">
        <UpdateMeddiccModal
          opportunityId={opportunityId}
          userId={userId}
          apiKey={apiKey}
          stage={stage}
          onClose={() => setShowUpdateMeddiccModal(false)}
        />
      </div>
      )}

      {/* Generate Tasks Modal */}
      {showGenerateTasksModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Generate Tasks</h3>
            <label htmlFor="avg-deal-time-days">Average Deal Time (days):</label>
            <input
              type="number"
              id="avg-deal-time-days"
              value={avgDealTimeDays}
              onChange={(e) => setAvgDealTimeDays(Number(e.target.value))}
              placeholder="Enter number of days"
              min="1"
            />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button
                onClick={async () => {
                  await generateTasks(opportunityId, avgDealTimeDays);
                  setShowGenerateTasksModal(false);
                }}
                className="save-button"
              >
                Generate Tasks
              </button>
              <button
                onClick={() => setShowGenerateTasksModal(false)}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {createTaskModalVisible && (
        <div className="modal">
          <div className="modal-content">
            <h3>Create New Task</h3>
            {/* Create task form */}
            <button
              onClick={() => setCreateTaskModalVisible(false)}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Opportunity;
